import type { BeatLineViteConfig } from '../api/types';

function visible(el: HTMLElement | null): unknown {
  if (!el) return null;
  let cs: CSSStyleDeclaration | null = null;
  try { cs = getComputedStyle(el); } catch (_) {}
  return {
    exists: true,
    display: el.style.display || '',
    computedDisplay: cs ? cs.display : '',
    hidden: !!el.hidden,
    ariaHidden: el.getAttribute('aria-hidden'),
    className: el.className,
    text: (el.textContent || '').slice(0, 180),
  };
}

function childrenCount(id: string): number | null {
  const el = document.getElementById(id);
  return el ? el.children.length : null;
}

function collect(config: BeatLineViteConfig): string {
  const state = (window.state || (window as any).__beatlineTsLastState || {}) as Record<string, any>;
  const zones: Record<string, unknown> = {};
  for (let i = 1; i <= 4; i += 1) zones[`zone-p${i}`] = visible(document.getElementById(`zone-p${i}`));
  const storage: Record<string, string> = {};
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i) || '';
      if (/beatline|spotify|heard|playback/i.test(key)) storage[key] = String(localStorage.getItem(key)).slice(0, 700);
    }
  } catch (error) {
    storage.error = String(error);
  }

  return JSON.stringify({
    c50LocalDebug: true,
    version: config.version,
    now: new Date().toISOString(),
    href: location.href,
    protocol: location.protocol,
    userAgent: navigator.userAgent,
    configNativeApp: config.nativeApp,
    configEntryContext: config.entryContext,
    remoteSiteUrl: config.remoteSiteUrl,
    appBootstrapUrl: config.appBootstrapUrl,
    appBootstrapJsonpUrl: config.appBootstrapJsonpUrl,
    bootstrapStatus: config.bootstrapStatus || null,
    settingsApp: config.beatlineSettings?.app || null,
    ajaxUrl: config.beatlineSettings?.ajaxUrl || null,
    hasNonce: !!config.beatlineSettings?.nonce,
    userLoggedIn: config.userLoggedIn,
    beatlineAppDataset: document.getElementById('beatline-app') ? { ...(document.getElementById('beatline-app') as HTMLElement).dataset } : null,
    selectedPlayerCount: (document.querySelector('input[name="p_count"]:checked') as HTMLInputElement | null)?.value || null,
    selectedSoloMode: (document.querySelector('input[name="solo_mode"]:checked') as HTMLInputElement | null)?.value || null,
    statePlayerCount: state?.playerCount,
    stateMode: state?.mode,
    stateSolo: { timeAttack: !!state?.timeAttack, speedrun: !!state?.speedrun, survival: !!state?.survival, perfectOrder: !!state?.perfectOrder },
    activeScorePanel: visible(document.getElementById('active-score-panel')),
    zones,
    timelines: {
      p1: childrenCount('timeline-p1'),
      p2: childrenCount('timeline-p2'),
      p3: childrenCount('timeline-p3'),
      p4: childrenCount('timeline-p4'),
    },
    functions: {
      beatlineHeardCommit: typeof (window as any).beatlineHeardCommit,
      beatlineHeardStats: typeof (window as any).beatlineHeardStats,
      beatlineSpotifyPlayTrack: typeof (window as any).beatlineSpotifyPlayTrack,
      BeatLineSpotify: !!(window as any).BeatLineSpotify,
      BeatLineNative: !!(window as any).BeatLineNative,
      BeatLineAndroidBilling: !!(window as any).BeatLineAndroidBilling,
      Capacitor: !!(window as any).Capacitor,
    },
    localStorage: storage,
    scripts: Array.from(document.scripts).map((script) => script.src || '[inline]').filter((src) => /beatline|vite|main|legacy|capacitor|assets/i.test(src)),
    styleTags: Array.from(document.querySelectorAll('style')).map((style) => style.id || '[inline-style]').filter((id) => /beatline|vite/i.test(id)),
  }, null, 2);
}

function shouldShowLocalDebug(config: BeatLineViteConfig): boolean {
  try {
    const w = window as any;
    const params = new URLSearchParams(window.location.search || '');

    // Expliciete lokale debugknop. Normale beatline_debug/app_diag mag deze knop niet meer automatisch tonen.
    if (params.get('beatline_local_debug') === '1') return true;
    if (params.get('local_debug') === '1') return true;
    if (w.BEATLINE_FORCE_LOCAL_DEBUG === true) return true;

    // Oude default: config.debug. Bewust NIET meer genoeg voor productie.
    return false;
  } catch (_) {
    return false;
  }
}

export function installLocalDebug(config: BeatLineViteConfig): void {
  if (!shouldShowLocalDebug(config)) return;
  if (document.getElementById('beatline-c50-dbg-btn')) return;

  const style = document.createElement('style');
  style.id = 'beatline-c50-local-debug-style';
  style.textContent = `
    #beatline-c50-dbg-btn{position:fixed!important;right:10px!important;bottom:10px!important;z-index:2147483647!important;background:#ff8a00!important;color:#101010!important;border:2px solid #fff!important;border-radius:999px!important;font:900 12px/1 Arial,sans-serif!important;padding:10px 12px!important;box-shadow:0 8px 28px rgba(0,0,0,.45)!important;}
    #beatline-c50-dbg-panel{position:fixed!important;inset:8px!important;z-index:2147483646!important;background:#07101f!important;color:#fff!important;border:2px solid #ff8a00!important;border-radius:16px!important;padding:12px!important;font-family:Arial,sans-serif!important;display:none!important;box-shadow:0 14px 80px rgba(0,0,0,.65)!important;}
    #beatline-c50-dbg-panel.is-open{display:block!important;}
    #beatline-c50-dbg-panel textarea{width:100%!important;height:calc(100vh - 140px)!important;background:#000!important;color:#0f0!important;border:1px solid rgba(255,255,255,.2)!important;border-radius:10px!important;padding:10px!important;font:12px/1.35 Consolas,monospace!important;box-sizing:border-box!important;}
    #beatline-c50-dbg-panel button{margin:0 6px 8px 0!important;padding:8px 10px!important;border-radius:10px!important;border:0!important;font-weight:800!important;}
  `;
  document.head.appendChild(style);

  const btn = document.createElement('button');
  btn.id = 'beatline-c50-dbg-btn';
  btn.type = 'button';
  btn.textContent = 'DBG48';

  const panel = document.createElement('div');
  panel.id = 'beatline-c50-dbg-panel';
  panel.innerHTML = '<div><strong>BeatLine Local Debug C50</strong></div><button id="c50-copy" type="button">Kopieer debug</button><button id="c50-refresh" type="button">Vernieuw</button><button id="c50-close" type="button">Sluit</button><textarea id="c50-output" spellcheck="false"></textarea><div id="c50-status"></div>';

  document.body.appendChild(btn);
  document.body.appendChild(panel);

  const out = panel.querySelector('#c50-output') as HTMLTextAreaElement;
  const status = panel.querySelector('#c50-status') as HTMLDivElement;
  const refresh = () => { out.value = collect(config); };
  btn.addEventListener('click', () => { panel.classList.add('is-open'); refresh(); });
  panel.querySelector('#c50-refresh')?.addEventListener('click', refresh);
  panel.querySelector('#c50-close')?.addEventListener('click', () => panel.classList.remove('is-open'));
  panel.querySelector('#c50-copy')?.addEventListener('click', async () => {
    refresh();
    out.select();
    try {
      await navigator.clipboard.writeText(out.value);
      status.textContent = 'Gekopieerd';
    } catch (_) {
      try { document.execCommand('copy'); status.textContent = 'Gekopieerd via selectie'; }
      catch (error) { status.textContent = 'KopiÃ«ren mislukt, selecteer handmatig'; }
    }
  });
}
